/**
 * @license
 * Copyright Google Inc. All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */


import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  Optional,
  ViewEncapsulation
} from '@angular/core';
import {coerceBooleanProperty} from '@angular/cdk/coercion';
import {
  trigger,
  state,
  style,
  animate,
  transition
} from '@angular/animations';
import {CdkColumnDef} from '@angular/cdk/table';
import {Subscription} from 'rxjs/Subscription';
import {merge} from 'rxjs/observable/merge';
import {MdSort, MdSortable} from './sort';
import {MdSortHeaderIntl} from './sort-header-intl';
import {getMdSortHeaderNotContainedWithinMdSortError} from './sort-errors';
import {AnimationCurves, AnimationDurations} from '@angular/material/core';

const /** @type {?} */ SORT_ANIMATION_TRANSITION =
    AnimationDurations.ENTERING + ' ' + AnimationCurves.STANDARD_CURVE;
/**
 * Applies sorting behavior (click to change sort) and styles to an element, including an
 * arrow to display the current sort direction.
 * 
 * Must be provided with an id and contained within a parent MdSort directive.
 * 
 * If used on header cells in a CdkTable, it will automatically default its id from its containing
 * column definition.
 */
export class MdSortHeader implements MdSortable {
private _rerenderSubscription: Subscription;
/**
 * ID of this sort header. If used within the context of a CdkColumnDef, this will default to
 * the column's name.
 */
id: string;
/**
 * Sets the position of the arrow that displays when sorted.
 */
arrowPosition: 'before' | 'after' = 'after';
/**
 * Overrides the sort start value of the containing MdSort for this MdSortable.
 */
start: 'asc' | 'desc';
/**
 * Overrides the disable clear value of the containing MdSort for this MdSortable.
 * @return {?}
 */
get disableClear() { return this._disableClear; }
/**
 * @param {?} v
 * @return {?}
 */
set disableClear(v) { this._disableClear = coerceBooleanProperty(v); }
private _disableClear: boolean;
/**
 * @return {?}
 */
get _id() { return this.id; }
/**
 * @param {?} v
 * @return {?}
 */
set _id(v: string) { this.id = v; }
/**
 * @param {?} _intl
 * @param {?} changeDetectorRef
 * @param {?} _sort
 * @param {?} _cdkColumnDef
 */
constructor(public _intl: MdSortHeaderIntl,
              changeDetectorRef: ChangeDetectorRef,
public _sort: MdSort,
public _cdkColumnDef: CdkColumnDef) {
    if (!_sort) {
      throw getMdSortHeaderNotContainedWithinMdSortError();
    }

    this._rerenderSubscription = merge(_sort.sortChange, _intl.changes).subscribe(() => {
      changeDetectorRef.markForCheck();
    });
  }
/**
 * @return {?}
 */
ngOnInit() {
    if (!this.id && this._cdkColumnDef) {
      this.id = this._cdkColumnDef.name;
    }

    this._sort.register(this);
  }
/**
 * @return {?}
 */
ngOnDestroy() {
    this._sort.deregister(this);
    this._rerenderSubscription.unsubscribe();
  }
/**
 * Whether this MdSortHeader is currently sorted in either ascending or descending order.
 * @return {?}
 */
_isSorted() {
    return this._sort.active == this.id && this._sort.direction;
  }
static decorators: DecoratorInvocation[] = [
{ type: Component, args: [{
  moduleId: module.id,
  selector: '[md-sort-header], [mat-sort-header]',
  templateUrl: 'sort-header.html',
  styleUrls: ['sort-header.css'],
  host: {
    '(click)': '_sort.sort(this)',
    '[class.mat-sort-header-sorted]': '_isSorted()',
  },
  encapsulation: ViewEncapsulation.None,
  preserveWhitespaces: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('indicator', [
      state('asc', style({transform: 'translateY(0px)'})),
      // 10px is the height of the sort indicator, minus the width of the pointers
      state('desc', style({transform: 'translateY(10px)'})),
      transition('asc <=> desc', animate(SORT_ANIMATION_TRANSITION))
    ]),
    trigger('leftPointer', [
      state('asc', style({transform: 'rotate(-45deg)'})),
      state('desc', style({transform: 'rotate(45deg)'})),
      transition('asc <=> desc', animate(SORT_ANIMATION_TRANSITION))
    ]),
    trigger('rightPointer', [
      state('asc', style({transform: 'rotate(45deg)'})),
      state('desc', style({transform: 'rotate(-45deg)'})),
      transition('asc <=> desc', animate(SORT_ANIMATION_TRANSITION))
    ])
  ]
}, ] },
];
/**
 * @nocollapse
 */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
{type: MdSortHeaderIntl, },
{type: ChangeDetectorRef, },
{type: MdSort, decorators: [{ type: Optional }, ]},
{type: CdkColumnDef, decorators: [{ type: Optional }, ]},
];
static propDecorators: {[key: string]: DecoratorInvocation[]} = {
'id': [{ type: Input, args: ['md-sort-header', ] },],
'arrowPosition': [{ type: Input },],
'start': [{ type: Input, args: ['start', ] },],
'disableClear': [{ type: Input },],
'_id': [{ type: Input, args: ['mat-sort-header', ] },],
};
}

function MdSortHeader_tsickle_Closure_declarations() {
/** @type {?} */
MdSortHeader.decorators;
/**
 * @nocollapse
 * @type {?}
 */
MdSortHeader.ctorParameters;
/** @type {?} */
MdSortHeader.propDecorators;
/** @type {?} */
MdSortHeader.prototype._rerenderSubscription;
/**
 * ID of this sort header. If used within the context of a CdkColumnDef, this will default to
 * the column's name.
 * @type {?}
 */
MdSortHeader.prototype.id;
/**
 * Sets the position of the arrow that displays when sorted.
 * @type {?}
 */
MdSortHeader.prototype.arrowPosition;
/**
 * Overrides the sort start value of the containing MdSort for this MdSortable.
 * @type {?}
 */
MdSortHeader.prototype.start;
/** @type {?} */
MdSortHeader.prototype._disableClear;
/** @type {?} */
MdSortHeader.prototype._intl;
/** @type {?} */
MdSortHeader.prototype._sort;
/** @type {?} */
MdSortHeader.prototype._cdkColumnDef;
}


interface DecoratorInvocation {
  type: Function;
  args?: any[];
}
